import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LogoutService } from '../services/logout.service';
import { UserService } from '../services/user/user.service';
import { RestApiService } from '../services/rest-api/rest-api.service';
import { isInputError, isRelativeUrl } from './functions';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '../services/local-storage.service';
import { LoggerService } from '../services/logger/logger.service';
import { AccessToken } from '../services/rest-api/rest-api';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<null | string> = new BehaviorSubject<null | string>(null);
  private localToken = '';

  public constructor(
    private logoutService: LogoutService,
    private userService: UserService,
    private restApiService: RestApiService,
    private localStorageService: LocalStorageService,
    private loggerService: LoggerService,
  ) {
    this.initTokenFromLocal();
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // do not intercept config img etc. OR login url (error handled separately)
    if(isRelativeUrl(req.url)
      || req.url.includes('loginBCAS')
      || req.url.includes('logout')
      || req.url.includes('blog/preview')
    ) {
      return next.handle(req);
    } else {
      return next.handle(req).pipe(
        catchError(error => {
          if (isInputError(error, 401, { includedErrorMessage: 'Missing or invalid access token.' })) {
            return this.handle401Error(req, next);
          }
          return throwError(() => error);
        }
      ));
    }
  }

  private initTokenFromLocal(): void {
    this.localStorageService.getItem({key: LOCAL_STORAGE_KEYS.TOKEN_LOCAL_STORAGE_KEY}).pipe(take(1)).subscribe({
      error: (err) => this.handleTokenError(err),
      next: (data) => this.handleTokenData(data),
    })
  }

  private handleTokenError(error: unknown): void {
    this.loggerService.error('Failed to load local token. ', error);
  }

  private handleTokenData(token: string): void {
    this.localToken = token;
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      if (this.localToken) {
        return this.restApiService.backendRefreshAuth(this.localToken).pipe(
          switchMap((token: AccessToken) => {
            this.isRefreshing = false;

            this.userService.updateBearer(token.access_token);
            this.refreshTokenSubject.next(token.access_token);

            return next.handle(this.addTokenHeader(request, token.access_token));
          }),
          catchError((err) => {
            this.isRefreshing = false;

            if(isInputError(err, 401)) {
              this.localToken = '';
              this.logoutService.logOut();
            }

            return throwError(() => err);
          })
        );
      } else {
        this.logoutService.logOut();
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<unknown>, token: string) {
    return request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + token) });
  }
}